"use client";

import Link from "next/link";
import React from "react";
import { Menu, ShoppingCart } from "lucide-react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { useCartStore } from "@/store/cart";
import { useAuthStore } from "@/store/auth";
import { useRouter } from "next/navigation";
import { BASE_URL } from "@/lib/constant";
import Image from "next/image";

const NavigationLink = [
  { label: "Home", href: "/" },
  { label: "Products", href: "/products" },
  { label: "Categories", href: "/categories" },
  { label: "Orders", href: "/orders" },
  { label: "Settings", href: "/settings" },
];

type AuthButtonType = {
  token: string | null;
  handleLogoutRequest: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
};

function AuthButtonMobile({ token, handleLogoutRequest }: AuthButtonType) {
  if (token) {
    return (
      <SheetClose asChild>
        <Button
          className="text-center flex w-full items-center py-6 text-lg font-semibold transition duration-300 cursor-pointer mt-5"
          variant="destructive"
          onClick={handleLogoutRequest}
        >
          Logout
        </Button>
      </SheetClose>
    );
  }

  return (
    <SheetClose asChild>
      <Button className="bg-green-800 text-center text-white flex w-full items-center py-6 text-lg font-semibold  hover:text-gray-100 hover:bg-green-700 transition duration-300 cursor-pointer mt-5">
        <Link href="/signup" className="w-full" prefetch={false}>
          SignUp
        </Link>
      </Button>
    </SheetClose>
  );
}

function AuthButton({ token, handleLogoutRequest }: AuthButtonType) {
  if (token) {
    return (
      <div className="hidden lg:flex items-center space-x-4">
        <Button
          className="text-white py-2 px-5 rounded-full bg-red-700 hover:bg-red-600 transition duration-300 font-semibold cursor-pointer"
          onClick={handleLogoutRequest}
        >
          Logout
        </Button>
      </div>
    );
  }

  return (
    <div className="hidden lg:flex items-center space-x-4">
      <Link
        href={"/signup"}
        className="bg-white py-1.5 px-4 rounded-full text-green-700 hover:bg-green-100 transition duration-300 font-semibold cursor-pointer"
      >
        SignUp
      </Link>
    </div>
  );
}

function Header() {
  const { items, clearCart } = useCartStore();
  const { token, clearAuth, hasHydrated } = useAuthStore();
  const router = useRouter();

  const handleLogoutRequest = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    if (hasHydrated && token) {
      try {
        await fetch(`${BASE_URL}/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }
    clearAuth();
    clearCart();
    router.push("/signin");
  };

  return (
    <header className="backdrop-blur-lg sticky top-0 z-50 shadow-md bg-[#2d6a4f]">
      <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
        <Link
          href="/"
          className="text-3xl font-bold text-white tracking-wider max-sm:text-xl"
        >
          <Image
            src="/logo.png"
            alt="logo"
            width={80}
            height={80}
            className="rounded-full inline mr-2 max-sm:w-12"
          />
          Ek Hundred
        </Link>
        <div className="flex space-x-8">
          <div className="hidden lg:flex items-center space-x-8">
            {NavigationLink.map((link, idx) => {
              if (
                (link.label === "Settings" || link.label === "Orders") &&
                !token
              ) {
                return;
              } else {
                return (
                  <Link
                    href={link.href}
                    key={idx}
                    className="text-white hover:text-green-200 transition duration-300"
                  >
                    {link.label}
                  </Link>
                );
              }
            })}
          </div>
          <div className="flex items-center space-x-4">
            <Link
              href="/cart"
              className="relative text-white hover:text-green-200 transition duration-300"
            >
              <ShoppingCart className="w-6 h-6" />
              {items.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 rounded-full">
                  {items.length}
                </span>
              )}
            </Link>
          </div>
          <AuthButton handleLogoutRequest={handleLogoutRequest} token={token} />
          <div className="lg:hidden flex">
            <Sheet>
              <SheetTrigger>
                <Menu className="text-white hover:text-green-200 transition duration-300 cursor-pointer" />
              </SheetTrigger>
              <SheetContent className="px-4 py-2">
                <SheetHeader className="px-0">
                  <SheetTitle className="text-xl">Ek Hundred</SheetTitle>
                  <SheetDescription></SheetDescription>
                </SheetHeader>
                <div className="grid gap-2">
                  {NavigationLink.map((link, idx) => {
                    if (
                      (link.label === "Settings" || link.label === "Orders") &&
                      !token
                    ) {
                      return;
                    } else {
                      return (
                        <SheetClose asChild key={idx}>
                          <Link
                            href={link.href}
                            className="flex w-full items-center py-2 text-lg font-semibold text-gray-800 hover:text-green-500 transition duration-300"
                            prefetch={false}
                          >
                            {link.label}
                          </Link>
                        </SheetClose>
                      );
                    }
                  })}
                  <AuthButtonMobile
                    handleLogoutRequest={handleLogoutRequest}
                    token={token}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Header;
